import { Popover, Button, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import styles from './PopoverReport.module.scss'
import { Delete2Icon, DoneAll} from '../../libs/corporate/icons/outlined/action/MainCollection';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export interface PopoverReportProps {
    label?: string;
    items: IGridRow[];
    onItemsChange: (items: IGridRow[]) => void;
    onClose?: () => void;
    canRemove?: boolean;
    showCloseButton?: boolean;
    closeOnClickOutside?: boolean;
}

export const PopoverReport: React.FC<PopoverReportProps> = (props) => {
    const { t } = useTranslation();
    const baseT = (value: string) => t('reports.reportsPopUp.' + value)
    const {
        label = baseT('selectedItems'),
        items,
        onItemsChange,
        onClose,
        canRemove = true,
        showCloseButton = true,
        closeOnClickOutside = false
    } = props;


    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (closeOnClickOutside) {
                if (wrapperRef.current && 
                    !wrapperRef.current.contains(event.target as Node) && 
                    open) {
                    setOpen(false);
                    onClose?.();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, closeOnClickOutside, onClose]);

    const toggleOpen = () => {
        setOpen(!open);
        if (open) {
            onClose?.();
        }
    };

    const hide = () => {
        setOpen(false);
        onClose?.();
    };

    const handleRemoveItem = (item: IGridRow, e: React.MouseEvent) => {
        e.stopPropagation();
        const newItems = items.filter(
            selectedItem => selectedItem.idGlobal !== item.idGlobal
        );
        onItemsChange(newItems);
    };

    const handleRemoveAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onItemsChange([]);
    };

    const handleCloseClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        hide();
    };

    const content = (
        <div className={styles.popoverContent} ref={popoverRef}>
            {label && (
                <div className={styles.popoverHeader}>
                    <p className={styles.label}>{label}</p>
                    {canRemove && items.length > 0 && (
                        <div className={styles.headerButtonWrapper}>
                            <Button
                                type="text"
                                size="middle"
                                icon={<Delete2Icon />}
                                onClick={handleRemoveAll}
                                className={styles.removeAllButton}
                                title={baseT('deleteAllItems')}
                            />
                            {showCloseButton && (
                                <Button
                                    type="text"
                                    size="middle"
                                    icon={<CloseOutlined />}
                                    onClick={handleCloseClick}
                                    className={styles.closeButton}
                                    title={baseT('close')}
                                />
                    )}
                        </div>
                    )}
                </div>
            )}

            <div className={styles.itemsList}>
                {items.length === 0 ? (
                    <p className={styles.emptyMessage}>{baseT('noSelectedItems')}</p>
                ) : (
                    items.map((item, index) => (
                        <div key={index} className={styles.itemRow}>
                            <span className={styles.itemName}>
                                {item.displayName}
                            </span>
                            {canRemove && (
                                <Button
                                    type="text"
                                    size="middle"
                                    icon={<CloseOutlined />}
                                    onClick={(e) => handleRemoveItem(item, e)}
                                    className={styles.removeItemButton}
                                    title={baseT('deleteItem')}
                                />
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    return (
        <div className={styles.popoverReportWrapper} ref={wrapperRef}>
            <Popover
                getPopupContainer={() => document.body}
                content={content}
                trigger="click"
                placement="rightTop"
                open={open}
                onOpenChange={(newOpen) => {
                    if (!closeOnClickOutside) {
                        return;
                    }
                    setOpen(newOpen);
                    if (!newOpen) {
                        onClose?.();
                    }
                }}
                destroyTooltipOnHide={false}
                overlayStyle={{
                    pointerEvents: 'auto'
                }}
            >
                <Tooltip
                    title={baseT('selectedItems')}
                    placement="bottom"
                    autoAdjustOverflow={false}
                    overlayStyle={{ maxWidth: 240 }}
                    getPopupContainer={() => document.body}
                >
                    <button
                        type="button"
                        className={styles.popoverReportButton}
                        onClick={toggleOpen}
                    >
                        <DoneAll />
                        {items.length > 0 && (
                            <span className={styles.itemsCount}>
                                {items.length}
                            </span>
                        )}
                    </button>                
                </Tooltip>
            </Popover>
        </div>
    );
};